"use client";

import type { TUser } from "@/app/(auth)/_utils/functions/getUser.ts";
import {
  COGNITO_PRESENTATION_MODE_ATTRIBUTE,
  isPresentationModeAttributeEnabled,
  userHasPresentationOrganizationAccess,
} from "@/app/_utils/constants/presentationMode.ts";
import { ErrorToastContext } from "@/app/_utils/context/ErrorToastContext.tsx";
import { useRequest } from "@/app/_utils/hooks/useRequest.ts";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useMemo } from "react";
import { UserContext } from "./UserDataContext.tsx";

export interface IPresentationModeContext {
  presentationModeEnabled: boolean;
  canUsePresentationMode: boolean;
  /** True only when the current view should apply chemistry blur. */
  presentationBlurActive: boolean;
}

export const PresentationModeContext = createContext<IPresentationModeContext>({
  presentationModeEnabled: false,
  canUsePresentationMode: false,
  presentationBlurActive: false,
});

/**
 * Provides presentation-mode state derived from the Cognito ID token.
 *
 * Toggling is done in the user menu via a full navigation to `/` so the layout
 * receives a fresh server session with no client-side optimistic overrides.
 *
 * Auto-revoke: when the token still says enabled but the user lost access to
 * the presentation org, the attribute is automatically set to false and the
 * router refreshes. A toast is shown on failure so the user can intervene.
 *
 * @param children - React subtree.
 *
 * @returns Context provider element.
 */
export default function PresentationModeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { user } = useContext(UserContext);
  const { setToastError } = useContext(ErrorToastContext);
  const { makeRequest } = useRequest(setToastError);
  const router = useRouter();
  const canUsePresentationMode = userHasPresentationOrganizationAccess(user);

  const userPresentationModeAttr = user?.[COGNITO_PRESENTATION_MODE_ATTRIBUTE];

  const cognitoPresentationEnabled = isPresentationModeAttributeEnabled(
    userPresentationModeAttr,
  );

  const userSub = user?.sub ?? "";

  useEffect(() => {
    if (
      userSub === "" ||
      canUsePresentationMode ||
      !isPresentationModeAttributeEnabled(userPresentationModeAttr)
    ) {
      return;
    }

    makeRequest<TUser | null>({
      requestSlug: "persist-presentation-mode-auto-off",
      url: "/api/v1/profile/update-profile",
      payload: [
        {
          Name: COGNITO_PRESENTATION_MODE_ATTRIBUTE,
          Value: "false",
        },
      ],
      callback: () => {
        router.refresh();
      },
    });
  }, [canUsePresentationMode, makeRequest, router, userPresentationModeAttr, userSub]);

  const value = useMemo(
    () => ({
      presentationModeEnabled: cognitoPresentationEnabled,
      canUsePresentationMode,
      presentationBlurActive: false,
    }),
    [cognitoPresentationEnabled, canUsePresentationMode],
  );

  return (
    <PresentationModeContext.Provider value={value}>
      {children}
    </PresentationModeContext.Provider>
  );
}
